import { InlineNotification } from '@lego/klik-ui';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { Loader, PageLoadError } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { SubNavigationBar } from '@/components/common/navbar/SubNavigationBar';
import { useDocumentTitle } from '@/hooks/common';
import { useWorkspace } from '@/hooks/workspace/workspace_resource';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { WorkspaceApplicationsSubPage } from '@/sub_pages/workspace/WorkspaceApplications';
import { WorkspaceAppWizardSubPage } from '@/sub_pages/workspace/WorkspaceAppWizardSubPage';
import { WorkspaceDevicesSubPage } from '@/sub_pages/workspace/WorkspaceDevices';
import { WorkspaceDeviceTransfersSubPage } from '@/sub_pages/workspace/WorkspaceDeviceTransfers';
import { WorkspaceEventConnectionsSubPage } from '@/sub_pages/workspace/WorkspaceEventConnections';
import { WorkspaceFilesSubPage } from '@/sub_pages/workspace/WorkspaceFiles';
import { WorkspaceFleetsSubPage } from '@/sub_pages/workspace/WorkspaceFleets';
import { WorkspaceHomeSubPage } from '@/sub_pages/workspace/WorkspaceHome';
import { WorkspaceNetworkConfigsSubPage } from '@/sub_pages/workspace/WorkspaceNetworkConfigs';
import { WorkspaceServicesSubPage } from '@/sub_pages/workspace/WorkspaceServices';
import { WorkspaceStatusSubPage } from '@/sub_pages/workspace/WorkspaceStatus';
import { paths } from '@/utility/global_paths';

const WorkspacePageContent = ({ workspaceId }: { workspaceId: number }) => {
  const { data, error, isLoading, isValidating, mutate } = useWorkspace(workspaceId);
  useRegisterPageContext(data);
  useDocumentTitle(data?.name ?? `Workspace #${workspaceId}`);

  if (isLoading) {
    return <Loader />;
  }

  if (error || !data) {
    return (
      <PageLoadError
        backAction={{ label: 'Back to workspaces', to: paths.workspaces }}
        onRetry={mutate}
      />
    );
  }

  const workspace = data;
  const isProvisioning = workspace.state !== 'READY';

  return (
    <>
      {isProvisioning && (
        <InlineNotification mb={4} variant={workspace.state === 'FAILED' ? 'error' : 'warning'}>
          <InlineNotification.Content>
            <InlineNotification.Title>
              {workspace.state === 'FAILED'
                ? 'Workspace provisioning failed'
                : 'Workspace is being provisioned'}
            </InlineNotification.Title>
            <InlineNotification.Description>
              {workspace.state === 'FAILED'
                ? 'Contact the EMMA team for support.'
                : 'Resources are being set up. Some features may be unavailable until provisioning completes.'}
            </InlineNotification.Description>
          </InlineNotification.Content>
        </InlineNotification>
      )}
      <BreadCrumb
        crumbs={[
          { label: 'Workspaces', path: paths.workspaces },
          { label: `Workspace #${workspace.id}`, path: paths.workspaceById(workspace.id) },
        ]}
      />
      {!isProvisioning && (
        <>
          <SubNavigationBar
            options={[
              { label: 'Home', path: paths.workspaceById(workspace.id) },
              { label: 'Devices', path: paths.workspaceDevices(workspace.id) },
              { label: 'Fleets', path: paths.workspaceFleets(workspace.id) },
              { label: 'Status', path: paths.workspaceStatus(workspace.id) },
              { label: 'Applications', path: paths.workspaceApplications(workspace.id) },
              { label: 'Files', path: paths.workspaceFiles(workspace.id) },
              { label: 'Network Configs', path: paths.workspaceNetworkConfigs(workspace.id) },
              { label: 'Services', path: paths.workspaceServices(workspace.id) },
              { label: 'Device Transfers', path: paths.workspaceDeviceTransfers(workspace.id) },
              {
                label: 'Event Connections',
                path: paths.workspaceEventConnections(workspace.id),
              },
            ]}
          />
          <Routes>
            <Route
              element={<WorkspaceHomeSubPage isLoading={isValidating} workspace={workspace} />}
              path="/"
            />
            <Route element={<WorkspaceDevicesSubPage workspace={workspace} />} path="devices" />
            <Route element={<WorkspaceFleetsSubPage workspace={workspace} />} path="fleets" />
            <Route
              element={<WorkspaceApplicationsSubPage workspace={workspace} />}
              path="applications"
            />
            <Route element={<WorkspaceFilesSubPage workspace={workspace} />} path="files" />
            <Route
              element={<WorkspaceNetworkConfigsSubPage workspace={workspace} />}
              path="network-configs"
            />
            <Route
              element={<WorkspaceServicesSubPage workspaceId={workspace.id} />}
              path="services"
            />
            <Route
              element={<WorkspaceDeviceTransfersSubPage workspace={workspace} />}
              path="device-transfers"
            />
            <Route
              element={<WorkspaceEventConnectionsSubPage workspace={workspace} />}
              path="event-connections"
            />
            <Route
              element={<WorkspaceAppWizardSubPage workspace={workspace} />}
              path="app-wizard"
            />
            <Route element={<WorkspaceStatusSubPage workspace={workspace} />} path="status" />
            <Route
              element={<Navigate replace to={paths.workspaceStatus(workspace.id)} />}
              path="workspace-status"
            />
          </Routes>
        </>
      )}
    </>
  );
};

export const WorkspacePage = () => {
  const { workspaceId } = useParams();

  if (!workspaceId || !Number.parseInt(workspaceId, 10)) {
    return <Navigate to={paths.notFound} />;
  }

  return <WorkspacePageContent workspaceId={Number.parseInt(workspaceId, 10)} />;
};
